import { regionLabelByState } from './constants';
import { buildDadosDisponibilidade } from './mappers';

export const sanitizePlate = value => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
export const sanitizeFleetNumber = value => String(value || '').trim().toUpperCase();

export const getPlateForPosition = item => {
  const candidates = [item?.placa, item?.veiculo];
  return candidates.map(sanitizePlate).find(value => value.length >= 5) || '';
};

export const getFleetNumberForPosition = item => {
  const candidates = [item?.numeroFrota, item?.frota];
  return candidates.map(sanitizeFleetNumber).find(Boolean) || '';
};

export const getPositionKeys = item => {
  const frota = getFleetNumberForPosition(item);
  const placa = getPlateForPosition(item);
  const keys = [];

  if (frota) keys.push(`frota:${frota}`);
  if (placa) keys.push(`placa:${placa}`);

  return keys;
};

export const buildPositionRequestPayload = item => {
  const idVeiculo = item?.idVeiculo ?? item?.codigo ?? item?.id ?? null;
  const placa = getPlateForPosition(item);
  const frota = getFleetNumberForPosition(item);

  return {
    ...(idVeiculo !== null && idVeiculo !== undefined && idVeiculo !== '' ? { idVeiculo } : {}),
    ...(placa ? { placa } : {}),
    ...(frota ? { frota } : {})
  };
};

export const findPositionBatchItemByPlate = (response, placa) => {
  const sanitizedTargetPlate = sanitizePlate(placa);

  return (response?.itens || []).find(item => sanitizePlate(item?.placa) === sanitizedTargetPlate) || null;
};

export const isPositionOlderThanOneDay = value => {
  if (!value) return false;
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() > 24 * 60 * 60 * 1000;
};

export const getPositionStatusFromPayload = posicao => {
  const latitude = Number(posicao?.latitude ?? posicao?.Latitude);
  const longitude = Number(posicao?.longitude ?? posicao?.Longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return 'missing';

  const dataPosicao = posicao?.dataPosicao ?? posicao?.DataPosicao ?? posicao?.dataAtualizacao ?? null;
  return isPositionOlderThanOneDay(dataPosicao) ? 'stale' : 'found';
};

export const getPositionDateFromPayload = posicao => posicao?.dataPosicao ?? posicao?.DataPosicao ?? posicao?.dataAtualizacao ?? null;

export const getPositionLocalFromPayload = posicao => {
  const cidade = posicao?.cidade ?? posicao?.Cidade ?? '';
  const estado = posicao?.estado ?? posicao?.Estado ?? '';
  const local = posicao?.local ?? posicao?.Local ?? '';

  if (local) return String(local);

  if (!cidade && !estado) return '';
  return `${cidade}${estado ? `/${estado}` : ''}`;
};

export const createMissingPositionEntry = () => ({
  status: 'missing',
  dataPosicao: null,
  local: '',
  checkedAt: Date.now(),
  posicao: null
});

export const createLoadingPositionEntry = () => ({
  status: 'loading',
  dataPosicao: null,
  local: '',
  checkedAt: Date.now(),
  posicao: null
});

export const isFinalPositionEntry = entry => Boolean(entry && entry.status && entry.status !== 'loading');

export const buildCachedPositionEntry = (response, placa) => {
  const posicao = response?.posicao || response || {};
  const latitude = Number(posicao?.latitude ?? posicao?.Latitude);
  const longitude = Number(posicao?.longitude ?? posicao?.Longitude);
  const dataPosicao = getPositionDateFromPayload(posicao);
  const local = getPositionLocalFromPayload(posicao);
  const cidade = posicao?.cidade ?? posicao?.Cidade ?? '';
  const estado = posicao?.estado ?? posicao?.Estado ?? '';
  const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
  const status = hasCoordinates && response?.encontrada !== false ? getPositionStatusFromPayload(posicao) : 'missing';
  const integracao = response?.integracao ?? response?.ultimaTecnologiaPosicao ?? posicao?.integracao ?? posicao?.Integracao ?? null;

  return {
    status,
    dataPosicao,
    local,
    checkedAt: Date.now(),
    posicao: hasCoordinates ? {
      latitude,
      longitude,
      placa: response?.placa || posicao?.placa || placa,
      dataPosicao,
      cidade,
      estado,
      integracao,
      status,
      local
    } : null
  };
};

export const buildPositionMapForItem = (item, entry) => getPositionKeys(item).reduce((acc, key) => {
  acc[key] = entry;
  return acc;
}, {});

export const mergePositionEntryIntoMap = (currentMap, item, entry) => ({
  ...(currentMap || {}),
  ...buildPositionMapForItem(item, entry)
});

export const getPositionEntryFromMap = (positionMap, item) => {
  const keys = getPositionKeys(item);

  for (const key of keys) {
    const entry = positionMap?.[key];

    if (entry) return entry;
  }

  return null;
};

export const attachPositionToItem = (item, positionMap) => {
  if (!item) return item;

  const entry = getPositionEntryFromMap(positionMap, item);

  return {
    ...item,
    posicao: entry?.posicao || null
  };
};

export const enrichDisponibilidadeWithPositions = (data, positionMap = {}) => {
  if (!data) return buildDadosDisponibilidade(null);

  return {
    ...data,
    posicoesPorChave: positionMap,
    frotasTracao: (data?.frotasTracao || []).map(item => attachPositionToItem(item, positionMap))
  };
};

export const stripTransientPositionData = data => ({
  motoristas: data?.motoristas || [],
  frotasTracao: (data?.frotasTracao || []).map(({ posicao, ...item }) => item),
  frotasReboque: (data?.frotasReboque || []).map(({ posicao, ...item }) => item),
  resumoFrotas: data?.resumoFrotas || []
});

export const getScopedPositionMap = (positionMap, items) => (items || []).reduce((acc, item) => {
  const entry = getPositionEntryFromMap(positionMap, item);

  if (!entry) return acc;

  return mergePositionEntryIntoMap(acc, item, entry);
}, {});

export const serializePositionMap = positionMap => Object.entries(positionMap || {}).reduce((acc, [key, entry]) => {
  if (!isFinalPositionEntry(entry)) return acc;

  acc[key] = {
    status: entry.status,
    dataPosicao: entry.dataPosicao ?? null,
    local: entry.local || '',
    checkedAt: typeof entry.checkedAt === 'number' ? entry.checkedAt : Date.now(),
    posicao: entry.posicao ? {
      latitude: entry.posicao.latitude ?? null,
      longitude: entry.posicao.longitude ?? null,
      placa: entry.posicao.placa || '',
      dataPosicao: entry.posicao.dataPosicao ?? null,
      cidade: entry.posicao.cidade || '',
      estado: entry.posicao.estado || '',
      integracao: entry.posicao.integracao ?? null,
      status: entry.posicao.status || entry.status,
      local: entry.posicao.local || entry.local || ''
    } : null
  };

  return acc;
}, {});

export const getPositionQueryTargets = items => {
  const targetsByPlate = new Map();

  (items || []).forEach(item => {
    const placa = getPlateForPosition(item);

    if (!placa || targetsByPlate.has(placa)) return;

    targetsByPlate.set(placa, item);
  });

  return [...targetsByPlate.values()];
};

const getErrorPayload = error => error?.response?.data || {};

const getErrorCode = error => {
  const payload = getErrorPayload(error);
  return payload?.error || payload?.code || error?.code || '';
};

const getErrorDetails = error => {
  const payload = getErrorPayload(error);
  return payload?.detalhes || payload?.details || {};
};

const getProviderFromError = error => {
  const payload = getErrorPayload(error);
  const details = getErrorDetails(error);
  const message = payload?.message || error?.message || '';
  const providerFromMessage = String(message).match(/Posicao\s+(.+?)\s+desatualizada/i)?.[1];

  return (
    payload?.integracao?.provider ||
    payload?.integracao?.Provider ||
    details?.provider ||
    details?.Provider ||
    providerFromMessage ||
    ''
  );
};

const getPositionDateFromError = error => {
  const payload = getErrorPayload(error);
  const details = getErrorDetails(error);

  return (
    details?.dataPosicao ??
    details?.DataPosicao ??
    payload?.dataPosicao ??
    payload?.DataPosicao ??
    null
  );
};

export const getPositionModalErrorMessage = error => {
  const status = error?.response?.status;
  const codigo = getErrorCode(error);
  const provider = getProviderFromError(error);
  const dataPosicao = getPositionDateFromError(error);

  if (codigo === 'PosicaoNaoEncontradaTodasFontes') {
    return 'Nao encontramos uma localizacao valida em nenhuma das integracoes configuradas para este veiculo.';
  }

  if (codigo === 'PosicaoDesatualizada') {
    const fonte = provider ? ` pela ${provider}` : '';
    const atualizacao = dataPosicao ? ` (atualizacao: ${dataPosicao})` : '';
    return `A localizacao retornada${fonte} esta desatualizada${atualizacao}.`;
  }

  if (codigo === 'CoordenadasZeradas') {
    const fonte = provider ? `A ${provider}` : 'A integracao';
    return `${fonte} retornou coordenadas 0,0, que nao representam uma localizacao valida do veiculo.`;
  }

  if (codigo === 'CoordenadasInvalidas') {
    return 'A integracao retornou coordenadas invalidas para este veiculo.';
  }

  return status === 404 || codigo === 'PlacaNaoEncontrada'
    ? 'Nao foi possivel encontrar a posicao dessa placa.'
    : 'Nao foi possivel consultar a localizacao.';
};

export const hasValidCoordinates = (latitude, longitude) => (
  Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude))
);

export const getRegionLabelFromState = value => {
  const normalizedValue = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();

  return regionLabelByState[normalizedValue] || '';
};

export const getPositionStatusForItem = (item, positionMap) => {
  const entry = getPositionEntryFromMap(positionMap, item);

  if (entry?.status) return entry.status;
  if (item?.posicao?.status) return item.posicao.status;
  if (!getPlateForPosition(item)) return 'missing';

  return 'loading';
};
